import React, { ReactNode } from "react";
import { IFieldProps, FieldProps } from "./IFieldProps";

export type ConditionOperator =
  | "equals"
  | "not_equals"
  | "gt"
  | "lt"
  | "gte"
  | "lte"
  | "contains"
  | "ncontains";

const parseCondition = (
  value: any,
  operator: ConditionOperator,
  conditionValue: any
): boolean => {
  if (!value) {
    return false;
  }

  if (typeof value !== typeof conditionValue) {
    throw new Error(
      `Conditional field had a type mismatch. The condition value supplied '${conditionValue}' was of type '${typeof conditionValue}' while the actual value '${value}' is of type '${typeof value}`
    );
  }
  switch (operator) {
    case "equals":
      return value === conditionValue;
    case "not_equals":
      return value !== conditionValue;
    case "gt":
      return value > conditionValue;
    case "lt":
      return value < conditionValue;
    case "gte":
      return value >= conditionValue;
    case "lte":
      return value <= conditionValue;
    case "contains":
      if (typeof value !== "string") {
        throw new Error(
          `Can't use the operator 'contains' on a value of type '${typeof value}'`
        );
      }

      return value.indexOf(conditionValue) > -1;
    case "ncontains":
      if (typeof value !== "string") {
        throw new Error(
          `Can't use the operator 'contains' on a value of type '${typeof value}'`
        );
      }

      return value.indexOf(conditionValue) === -1;
  }
};

export interface IConditionalFieldProps extends IFieldProps {
  conditionOperator: ConditionOperator;
  conditionValue: any;
  children: ReactNode;
  expression?: (...args: any[]) => boolean;
}

export const ForTrue = (props: { children: ReactNode }) => {
  return <>{passPropsToChildren(props)}</>;
};

export const ForFalse = (props: { children: ReactNode }) => {
  return <>{passPropsToChildren(props)}</>;
};

const ConditionalField = (props: IConditionalFieldProps) => {
  if (!props.record) {
    return null;
  }

  if (!props.source && !props.expression) {
    console.warn(
      "Using ConditionalField without supplying source or expression props"
    );
    return null;
  }

  const result = props.expression
    ? props.expression(props.record)
    : parseCondition(
        props.record[props.source!],
        props.conditionOperator,
        props.conditionValue
      );

  let elementsToRender = props.children;
  if (!result) {
    elementsToRender = React.Children.map(props.children, (child) => {
      if (
        React.isValidElement(child) &&
        (child as React.ReactElement<any>).type === ForFalse
      ) {
        return getChildWithPassedProps(child, props);
      }
    });
  } else {
    elementsToRender = React.Children.map(props.children, (child) => {
      if (
        React.isValidElement(child) &&
        (child as React.ReactElement<any>).type === ForTrue
      ) {
        return getChildWithPassedProps(child, props);
      }
    });
  }

  return <>{elementsToRender}</>;
};

function extract<T>(properties: Record<keyof T, any>) {
  return function <TActual extends T>(value: TActual) {
    let result = {} as T;
    for (const property of Object.keys(properties) as Array<keyof T>) {
      result[property] = value[property];
    }
    return result;
  };
}

const extractFieldProps = extract<IFieldProps>(new FieldProps());

const getChildWithPassedProps = (
  child: React.ReactElement<any>,
  props: any
) => {
  const newProps = extractFieldProps(props);
  return React.cloneElement(child, newProps);
};

const passPropsToChildren = (props: any) => {
  return React.Children.map(props.children, (child) => {
    if (React.isValidElement(child)) {
      return getChildWithPassedProps(child, props);
    }
  });
};

export default ConditionalField;
